/* Aura: carrosséis, vídeos, acordeões, animações. Vanilla JS, sem dependências. */
(() => {
  const root = document.documentElement;
  const reducedMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopMq = window.matchMedia('(min-width: 750px)');
  const reduced = () => reducedMq.matches;
  const isDesignMode = () => root.classList.contains('shopify-design-mode');
  const hasIO = 'IntersectionObserver' in window;

  // Distingue mouse de teclado para o contorno de foco do header
  document.addEventListener('pointerdown', () => (root.dataset.auraInput = 'pointer'), true);
  document.addEventListener('keydown', () => (root.dataset.auraInput = 'keyboard'), true);

  const each = (scope, selector, fn) => {
    if (scope.matches?.(selector)) fn(scope);
    scope.querySelectorAll(selector).forEach(fn);
  };

  /* ------------------------------------------------------------------
     Carrossel: setas, progresso, arraste com o mouse
  ------------------------------------------------------------------ */
  const initCarousel = (carousel) => {
    if (carousel.dataset.auraReady) return;
    const track = carousel.querySelector('[data-aura-track]');
    if (!track) return;
    carousel.dataset.auraReady = 'true';

    const prev = carousel.querySelector('[data-aura-prev]');
    const next = carousel.querySelector('[data-aura-next]');
    const bar = carousel.querySelector('[data-aura-progress]');

    const step = () => {
      const card = track.firstElementChild;
      if (!card) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const scroll = (dir) =>
      track.scrollBy({ left: dir * step(), behavior: reduced() ? 'auto' : 'smooth' });

    const update = () => {
      const total = track.scrollWidth - track.clientWidth;
      track.classList.toggle('is-scrollable', total > 1);
      if (prev) prev.disabled = track.scrollLeft <= 0;
      if (next) next.disabled = track.scrollLeft >= total - 1;
      if (bar) {
        const ratio = track.scrollWidth ? Math.min(track.clientWidth / track.scrollWidth, 1) : 1;
        const progress = total > 0 ? track.scrollLeft / total : 0;
        bar.style.setProperty('--aura-bar-size', `${ratio * 100}%`);
        bar.style.setProperty('--aura-bar-pos', `${progress * (1 - ratio) * 100}%`);
      }
    };

    prev?.addEventListener('click', () => scroll(-1));
    next?.addEventListener('click', () => scroll(1));
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    // Arraste com o mouse (desktop)
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = false;

    track.addEventListener('dragstart', (event) => event.preventDefault());

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth + 1) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }
      if (moved) track.scrollLeft = startScroll - dx;
    });

    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      track.classList.remove('is-dragging');
      try {
        track.releasePointerCapture(event.pointerId);
      } catch (e) {}
      const size = step();
      if (size) {
        track.scrollTo({ left: Math.round(track.scrollLeft / size) * size, behavior: reduced() ? 'auto' : 'smooth' });
      }
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    // Evita que o arraste dispare cliques em links
    track.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }
      },
      true
    );
  };

  /* ------------------------------------------------------------------
     Carrossel de creators: rolagem contínua, infinita, arraste e vídeo no próprio card
  ------------------------------------------------------------------ */
  const initUgc = (section) => {
    if (section.dataset.auraReady) return;
    const viewport = section.querySelector('[data-aura-ugc-viewport]');
    const rail = section.querySelector('[data-aura-ugc-rail]');
    if (!viewport || !rail) return;
    section.dataset.auraReady = 'true';

    const originals = [...rail.children];
    if (!originals.length) return;
    const prev = section.querySelector('[data-aura-prev]');
    const next = section.querySelector('[data-aura-next]');

    const SPEED = 40; // px por segundo
    let offset = 0;
    let setWidth = 0;
    let stepWidth = 0;
    let tween = null;
    let raf = 0;
    let last = 0;
    let inView = true;
    const holds = new Set();
    let releaseTimer;

    const wrap = () => {
      if (setWidth > 0) offset = ((offset % setWidth) + setWidth) % setWidth;
    };
    const apply = () => {
      rail.style.transform = `translate3d(${-offset}px, 0, 0)`;
    };

    // Duplica os cards o bastante para o loop nunca mostrar vazio
    const build = () => {
      deactivate();
      rail.querySelectorAll('[data-aura-clone]').forEach((clone) => clone.remove());
      const first = originals[0];
      const second = originals[1];
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      stepWidth = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth + gap;
      setWidth = stepWidth * originals.length;
      const copies = setWidth ? Math.ceil(viewport.clientWidth / setWidth) + 1 : 1;
      for (let i = 0; i < copies; i += 1) {
        originals.forEach((card) => {
          const clone = card.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-aura-clone', '');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('button, a, video').forEach((el) => el.setAttribute('tabindex', '-1'));
          // Os clones também só carregam o vídeo quando ficam visíveis
          clone.querySelectorAll('video').forEach((video) => {
            video.removeAttribute('src');
            video.querySelectorAll('source').forEach((source) => {
              const url = source.getAttribute('src') || source.dataset.src;
              if (!url) return;
              source.dataset.src = url;
              source.removeAttribute('src');
            });
            video.setAttribute('data-aura-lazy-video', '');
          });
          rail.appendChild(clone);
        });
      }
      wrap();
      apply();
      initVideos(rail);
    };

    const autoplay = () => !reduced() && holds.size === 0 && inView && !document.hidden && !tween;

    const frame = (now) => {
      const dt = Math.min(now - last, 64);
      last = now;
      if (tween) {
        const t = Math.min((now - tween.start) / tween.duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        offset = tween.from + (tween.to - tween.from) * eased;
        if (t >= 1) tween = null;
      } else if (autoplay()) {
        offset += (SPEED * dt) / 1000;
      }
      wrap();
      apply();
      raf = requestAnimationFrame(frame);
    };

    const start = () => {
      if (raf) return;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const hold = (key) => holds.add(key);
    const release = (key, delay = 0) => {
      clearTimeout(releaseTimer);
      if (delay) releaseTimer = setTimeout(() => holds.delete(key), delay);
      else holds.delete(key);
    };

    // Vídeo ativo: toca no próprio card, com som; só um por vez
    let active = null;
    let activeObserver = null;

    const syncSound = (card, video) => {
      const sound = card.querySelector('[data-aura-ugc-sound]');
      card.classList.toggle('is-muted', video.muted);
      sound?.setAttribute('aria-label', (video.muted ? sound.dataset.labelOn : sound.dataset.labelOff) || '');
    };

    const deactivate = () => {
      if (!active) return;
      const { card, video } = active;
      active = null;
      activeObserver?.disconnect();
      activeObserver = null;
      holds.delete('video');
      card.classList.remove('is-playing', 'is-muted');
      const toggle = card.querySelector('[data-aura-ugc-toggle]');
      toggle?.setAttribute('aria-label', toggle.dataset.labelPlay || '');
      video.muted = true;
      video.loop = true;
      video.play().catch(() => {});
    };

    const activate = (card) => {
      const video = card.querySelector('video');
      if (!video) return;
      if (active) deactivate();
      tween = null;
      holds.add('video');
      if (video.querySelector('source[data-src]')) loadVideo(video);
      video.muted = false;
      video.loop = false;
      video.currentTime = 0;
      video.play().catch(() => {
        // Sem permissão para tocar com som: segue mudo
        video.muted = true;
        video.play().catch(() => {});
        syncSound(card, video);
      });
      card.classList.add('is-playing');
      const toggle = card.querySelector('[data-aura-ugc-toggle]');
      toggle?.setAttribute('aria-label', toggle.dataset.labelPause || '');
      active = { card, video };
      syncSound(card, video);

      if (hasIO) {
        activeObserver = new IntersectionObserver(
          ([entry]) => {
            if (!entry.isIntersecting && active?.card === card) deactivate();
          },
          { threshold: 0.2 }
        );
        activeObserver.observe(card);
      }
    };

    // Vídeo terminou: volta ao preview e retoma a rolagem (media events não borbulham)
    viewport.addEventListener(
      'ended',
      (e) => {
        if (active && e.target === active.video) deactivate();
      },
      true
    );

    const go = (dir) => {
      const from = ((offset % setWidth) + setWidth) % setWidth;
      offset = from;
      const to = from + dir * stepWidth;
      if (reduced()) {
        offset = to;
        wrap();
        apply();
        return;
      }
      tween = { from, to, start: performance.now(), duration: 550 };
    };

    prev?.addEventListener('click', () => {
      deactivate();
      go(-1);
    });
    next?.addEventListener('click', () => {
      deactivate();
      go(1);
    });

    // Pausas: mouse sobre o carrossel, foco, aba oculta, vídeo tocando
    viewport.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && hold('hover'));
    viewport.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && release('hover'));
    // Só foco por teclado pausa; o foco do clique não deve travar a rolagem
    viewport.addEventListener('focusin', (e) => e.target.matches?.(':focus-visible') && hold('focus'));
    viewport.addEventListener('focusout', () => release('focus'));

    // Arraste (mouse e dedo)
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startOffset = 0;

    viewport.addEventListener('dragstart', (e) => e.preventDefault());
    viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startOffset = offset;
      tween = null;
      if (e.pointerType !== 'mouse') hold('touch');
    });
    viewport.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) {
        moved = true;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture(e.pointerId);
      }
      if (moved) {
        offset = startOffset - dx;
        wrap();
        apply();
      }
    });
    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;
      viewport.classList.remove('is-dragging');
      try {
        viewport.releasePointerCapture(e.pointerId);
      } catch (err) {}
      if (e.pointerType !== 'mouse') release('touch', 1200);
    };
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    // Clique no card: toca/pausa o vídeo ali mesmo (ignorado se foi arraste)
    viewport.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
          return;
        }
        const card = e.target.closest('.aura-ugc-card');
        if (!card) return;
        if (e.target.closest('[data-aura-ugc-sound]')) {
          if (active?.card !== card) return;
          active.video.muted = !active.video.muted;
          syncSound(card, active.video);
          return;
        }
        if (!e.target.closest('[data-aura-ugc-toggle]')) return;
        if (active?.card === card) deactivate();
        else activate(card);
      },
      true
    );

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop();
      else if (inView) start();
    });

    if (hasIO) {
      new IntersectionObserver(([entry]) => {
        inView = entry.isIntersecting;
        if (inView && !document.hidden) start();
        else stop();
      }).observe(section);
    }

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(build, 150);
    });

    build();
    start();
  };

  /* ------------------------------------------------------------------
     Produto principal: galeria, variantes, quantidade e barra fixa
  ------------------------------------------------------------------ */
  const initProductMain = (section) => {
    if (section.dataset.auraReady) return;
    section.dataset.auraReady = 'true';

    // Galeria
    const track = section.querySelector('[data-aura-pm-slides]');
    const slides = [...section.querySelectorAll('[data-aura-pm-slide]')];
    const thumbs = [...section.querySelectorAll('[data-aura-pm-thumb]')];
    const dots = [...section.querySelectorAll('.aura-pm__dot')];
    const thumbsBox = section.querySelector('.aura-pm__thumbs');
    let index = 0;

    const mark = () => {
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    const show = (i) => {
      if (!slides.length) return;
      index = (i + slides.length) % slides.length;
      mark();
      if (!desktopMq.matches && track) {
        track.scrollTo({ left: index * track.clientWidth, behavior: reduced() ? 'auto' : 'smooth' });
      }
      const thumb = thumbs[index];
      if (desktopMq.matches && thumb && thumbsBox) {
        thumbsBox.scrollTo({
          left: thumb.offsetLeft - (thumbsBox.clientWidth - thumb.clientWidth) / 2,
          behavior: reduced() ? 'auto' : 'smooth',
        });
      }
    };

    section.querySelector('[data-aura-pm-prev]')?.addEventListener('click', () => show(index - 1));
    section.querySelector('[data-aura-pm-next]')?.addEventListener('click', () => show(index + 1));
    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));

    track?.addEventListener(
      'scroll',
      () => {
        if (desktopMq.matches || !track.clientWidth) return;
        const next = Math.round(track.scrollLeft / track.clientWidth);
        if (next !== index && slides[next]) {
          index = next;
          mark();
        }
      },
      { passive: true }
    );

    // Quantidade
    const qty = section.querySelector('[data-aura-pm-qty-input]');
    const clamp = () => {
      if (qty) qty.value = String(Math.max(1, parseInt(qty.value, 10) || 1));
    };
    section.querySelector('[data-aura-pm-minus]')?.addEventListener('click', () => {
      if (qty) {
        qty.value = String(Math.max(1, (parseInt(qty.value, 10) || 1) - 1));
      }
    });
    section.querySelector('[data-aura-pm-plus]')?.addEventListener('click', () => {
      if (qty) {
        qty.value = String((parseInt(qty.value, 10) || 1) + 1);
      }
    });
    qty?.addEventListener('change', clamp);

    // Variantes
    const variantsEl = section.querySelector('[data-aura-pm-variants]');
    const groups = [...section.querySelectorAll('[data-aura-pm-option]')];
    const idInput = section.querySelector('[data-aura-pm-id]');
    const atc = section.querySelector('[data-aura-pm-atc] button');
    const barAdd = section.querySelector('[data-aura-pm-bar-add]');
    const barPrice = section.querySelector('[data-aura-pm-bar-price]');
    const priceEl = section.querySelector('[data-aura-pm-price]');
    let variants = [];
    try {
      variants = JSON.parse(variantsEl?.textContent || '[]');
    } catch (e) {}

    if (groups.length && variants.length) {
      const apply = () => {
        const chosen = groups.map((group) => group.querySelector('.aura-pm__pill.is-active')?.dataset.value);
        const match = variants.find((variant) => variant.options.every((value, i) => value === chosen[i]));
        if (!match) return;
        if (idInput) idInput.value = String(match.id);
        if (atc) atc.disabled = !match.available;
        if (barAdd) barAdd.disabled = !match.available;
        if (barPrice) barPrice.textContent = match.price;
        const now = priceEl?.querySelector('.aura-pm__price-now');
        if (now) now.textContent = match.price;
        let was = priceEl?.querySelector('.aura-pm__price-was');
        if (match.compare) {
          if (!was && now) {
            was = document.createElement('s');
            was.className = 'aura-pm__price-was';
            now.after(was);
          }
          if (was) was.textContent = match.compare;
        } else {
          was?.remove();
        }
        if (match.image) {
          const target = slides.findIndex((slide) => slide.dataset.imageId === String(match.image));
          if (target >= 0) show(target);
        }
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          window.history.replaceState({}, '', url);
        } catch (e) {}
      };

      groups.forEach((group) => {
        group.querySelectorAll('.aura-pm__pill').forEach((pill) => {
          pill.addEventListener('click', () => {
            group.querySelectorAll('.aura-pm__pill').forEach((other) => {
              other.classList.toggle('is-active', other === pill);
              other.setAttribute('aria-pressed', other === pill ? 'true' : 'false');
            });
            apply();
          });
        });
      });
    }

    // Barra fixa (mobile): aparece quando o botão principal passa para cima da tela
    const bar = section.querySelector('[data-aura-pm-bar]');
    const atcWrap = section.querySelector('[data-aura-pm-atc]');
    if (bar && atcWrap && hasIO) {
      new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting || entry.boundingClientRect.top >= 0;
      }).observe(atcWrap);
    }
    barAdd?.addEventListener('click', () => section.querySelector('form')?.requestSubmit());
  };

  /* ------------------------------------------------------------------
     Pontos de venda: busca e filtro por UF em tempo real
  ------------------------------------------------------------------ */
  const plain = (text) =>
    String(text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const initLocator = (section) => {
    if (section.dataset.auraReady) return;
    section.dataset.auraReady = 'true';
    const input = section.querySelector('[data-aura-locator-search]');
    const pills = [...section.querySelectorAll('[data-aura-locator-state]')];
    const cards = [...section.querySelectorAll('.aura-store')];
    const empty = section.querySelector('[data-aura-locator-empty]');
    let state = '';

    const apply = () => {
      const query = plain(input?.value).trim();
      let visible = 0;
      cards.forEach((card) => {
        const okState = !state || card.dataset.state === state;
        const okText = !query || plain(card.dataset.search).includes(query);
        const show = okState && okText;
        card.hidden = !show;
        if (show) visible += 1;
      });
      if (empty) empty.hidden = visible > 0;
    };

    input?.addEventListener('input', apply);
    pills.forEach((pill) =>
      pill.addEventListener('click', () => {
        state = pill.dataset.auraLocatorState;
        pills.forEach((other) => {
          other.classList.toggle('is-active', other === pill);
          other.setAttribute('aria-pressed', other === pill ? 'true' : 'false');
        });
        apply();
      })
    );
    apply();
  };

  /* ------------------------------------------------------------------
     Vídeos: carrega só ao entrar na viewport
  ------------------------------------------------------------------ */
  const loadVideo = (video) => {
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });
    video.load();
    video.play().catch(() => {});
  };

  const initVideos = (scope) => {
    const videos = scope.querySelectorAll('video[data-aura-lazy-video]');
    if (!videos.length) return;
    if (!hasIO) {
      videos.forEach(loadVideo);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          loadVideo(entry.target);
        }),
      { rootMargin: '200px' }
    );
    videos.forEach((video) => {
      video.removeAttribute('data-aura-lazy-video');
      observer.observe(video);
    });
  };

  /* ------------------------------------------------------------------
     Acordeões (<details>) com altura animada via grid-template-rows
  ------------------------------------------------------------------ */
  const ACCORDION_ITEMS =
    '.aura-faq__item, .aura-moment__item, .aura-highlight__item, .aura-lifestyle__item, .aura-pm__acc';
  const KEEP_OPEN = '.aura-moment__item';

  const openItem = (item) => {
    item.open = true;
    void item.offsetHeight;
    item.classList.add('is-open');
    item.dispatchEvent(new CustomEvent('aura:accordion-open', { bubbles: true }));
  };

  const closeItem = (item) => {
    if (!item.classList.contains('is-open')) return;
    item.classList.remove('is-open');
    const panel = item.querySelector(':scope > .aura-acc-panel');
    const finish = () => {
      if (!item.classList.contains('is-open')) item.open = false;
    };
    if (reduced() || !panel) {
      finish();
      return;
    }
    panel.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === panel) finish();
      },
      { once: true }
    );
    setTimeout(finish, 450);
  };

  const initAccordions = (scope) => {
    each(scope, ACCORDION_ITEMS, (item) => {
      if (item.dataset.auraReady) return;
      item.dataset.auraReady = 'true';
      const summary = item.querySelector(':scope > summary');
      if (!summary) return;

      const panel = document.createElement('div');
      panel.className = 'aura-acc-panel';
      const inner = document.createElement('div');
      inner.className = 'aura-acc-inner';
      [...item.children].filter((child) => child !== summary).forEach((child) => inner.appendChild(child));
      panel.appendChild(inner);
      item.appendChild(panel);
      if (item.open) item.classList.add('is-open');

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (item.classList.contains('is-open')) {
          if (!item.matches(KEEP_OPEN)) closeItem(item);
          return;
        }
        const group = item.closest('section') || document;
        group.querySelectorAll(ACCORDION_ITEMS).forEach((other) => other !== item && closeItem(other));
        openItem(item);
      });
    });
  };

  /* "Para cada momento": troca a imagem conforme o item aberto */
  const initMoment = (section) => {
    if (section.dataset.auraReady) return;
    section.dataset.auraReady = 'true';
    const panels = [...section.querySelectorAll('[data-aura-panel]')];

    section.addEventListener('aura:accordion-open', (event) => {
      const index = event.target.dataset?.auraItem;
      if (index === undefined) return;
      panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.auraPanel === index));
    });

    section.addEventListener('shopify:block:select', (event) => {
      const item = event.target.closest?.('details[data-aura-item]');
      if (item && !item.classList.contains('is-open')) item.querySelector('summary')?.click();
    });
  };

  /* Catálogo: drawer de filtros */
  const initFilters = (scope) => {
    each(scope, '[data-aura-filters-root]', (root) => {
      if (root.dataset.auraReady) return;
      root.dataset.auraReady = 'true';
      const dialog = root.querySelector('[data-aura-filters]');
      if (!dialog) return;
      root.querySelector('[data-aura-filters-open]')?.addEventListener('click', () => dialog.showModal());
      dialog.querySelector('[data-aura-filters-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });
  };

  /* Aba de oferta do hero */
  const initOffer = (scope) => {
    each(scope, '[data-aura-offer]', (offer) => {
      if (offer.dataset.auraReady) return;
      offer.dataset.auraReady = 'true';
      const key = 'aura-offer-closed';
      try {
        if (sessionStorage.getItem(key) && !isDesignMode()) {
          offer.hidden = true;
          return;
        }
      } catch (e) {}
      offer.querySelector('[data-aura-offer-close]')?.addEventListener('click', () => {
        offer.hidden = true;
        try {
          sessionStorage.setItem(key, '1');
        } catch (e) {}
      });
    });
  };

  /* ------------------------------------------------------------------
     Reveal ao rolar
  ------------------------------------------------------------------ */
  const HEAD_SELECTOR =
    '.aura-faq__eyebrow, .aura-lifestyle__eyebrow, .aura-title, .aura-ugc__sub, .aura-faq__sub, .aura-compare__sub, .aura-bundle__text, .aura-highlight__text';
  const REVEAL_SELECTOR =
    '.aura-moment__media, .aura-bundle__media, .aura-highlight__media, .aura-lifestyle__media, .aura-compare__scroll, .aura-ugc__controls, .aura-range__controls, .aura-people__controls, .aura-ugc__viewport';
  const STAGGER_SELECTOR =
    '.aura-range__track, .aura-why__track, .aura-benefits__grid, .aura-moment__list, .aura-faq__list, .aura-people__track, .aura-lifestyle__items, .aura-bundle__stats, .aura-footer__top';

  const STEP = 90;

  const tagSections = (scope) => {
    each(scope, '.aura-section, .aura-footer', (section) => {
      if (section.dataset.auraTagged) return;
      section.dataset.auraTagged = 'true';

      let order = 0;
      section.querySelectorAll(HEAD_SELECTOR).forEach((el) => {
        el.setAttribute('data-aura-reveal', '');
        el.style.setProperty('--aura-delay', `${order * STEP}ms`);
        order += 1;
      });

      section.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
        el.setAttribute('data-aura-reveal', '');
        el.style.setProperty('--aura-delay', `${order * STEP}ms`);
      });

      section.querySelectorAll(STAGGER_SELECTOR).forEach((el) => {
        el.setAttribute('data-aura-stagger', '');
        el.style.setProperty('--aura-base', `${order * STEP}ms`);
        [...el.children].forEach((child, i) => child.style.setProperty('--aura-i', i));
      });
    });
  };

  const reveal = (el) => {
    el.classList.add('is-visible');
    setTimeout(() => el.classList.add('is-done'), 2200);
  };

  const revealObserver = hasIO
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            revealObserver.unobserve(entry.target);
            reveal(entry.target);
          }),
        { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }
      )
    : null;

  const initReveal = (scope) => {
    tagSections(scope);
    const targets = [];
    each(scope, '[data-aura-reveal], [data-aura-stagger]', (el) => {
      if (!el.classList.contains('is-visible') && !targets.includes(el)) targets.push(el);
    });
    targets.forEach((el) => {
      if (!revealObserver || reduced() || isDesignMode()) reveal(el);
      else revealObserver.observe(el);
    });
  };

  /* ------------------------------------------------------------------
     Traços que se desenham (ícones de benefícios)
  ------------------------------------------------------------------ */
  const drawObserver = hasIO
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            drawObserver.unobserve(entry.target);
            const svg = entry.target;
            const index = Number(svg.dataset.auraDrawIndex || 0);
            svg.querySelectorAll('path, circle, line, rect, polyline').forEach((shape) => {
              shape.style.transition = `stroke-dashoffset 1200ms cubic-bezier(.22,1,.36,1) ${index * 120}ms`;
              shape.style.strokeDashoffset = '0';
            });
          }),
        { threshold: 0.4 }
      )
    : null;

  const initDraw = (scope) => {
    if (!drawObserver || reduced() || isDesignMode()) return;
    each(scope, 'svg[data-aura-draw]', (svg) => {
      if (svg.dataset.auraReady) return;
      svg.dataset.auraReady = 'true';
      const parent = svg.closest('.aura-benefit');
      const siblings = parent ? [...parent.parentElement.children] : [];
      svg.dataset.auraDrawIndex = parent ? siblings.indexOf(parent) : 0;
      svg.querySelectorAll('path, circle, line, rect, polyline').forEach((shape) => {
        let length = 0;
        try {
          length = shape.getTotalLength();
        } catch (e) {}
        if (!length) return;
        shape.style.strokeDasharray = `${length}`;
        shape.style.strokeDashoffset = `${length}`;
      });
      drawObserver.observe(svg);
    });
  };

  /* ------------------------------------------------------------------
     Contagem de números
  ------------------------------------------------------------------ */
  const NUMBER_PATTERN = /^(\D*?)(\d+(?:[.,]\d+)?)(\D*)$/;

  const countObserver = hasIO
    ? new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            countObserver.unobserve(entry.target);
            countUp(entry.target);
          }),
        { threshold: 0.6 }
      )
    : null;

  const countUp = (el) => {
    const match = el.dataset.auraCountText.match(NUMBER_PATTERN);
    if (!match) return;
    const [, prefix, raw, suffix] = match;
    const separator = raw.includes(',') ? ',' : '.';
    const decimals = raw.includes(separator) ? raw.split(separator)[1].length : 0;
    const target = parseFloat(raw.replace(',', '.'));
    const duration = 1200;
    const start = performance.now();

    const frame = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = (target * eased).toFixed(decimals).replace('.', separator);
      el.textContent = `${prefix}${value}${suffix}`;
      if (t < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };

  const initCount = (scope) => {
    if (!countObserver || reduced() || isDesignMode()) return;
    each(scope, '.aura-bundle__stat-number, .aura-highlight__value, [data-aura-count]', (el) => {
      if (el.dataset.auraReady) return;
      const text = el.textContent.trim();
      if (!NUMBER_PATTERN.test(text)) return;
      el.dataset.auraReady = 'true';
      el.dataset.auraCountText = text;
      const match = text.match(NUMBER_PATTERN);
      el.textContent = `${match[1]}${(0).toFixed(match[2].split(/[.,]/)[1]?.length || 0)}${match[3]}`;
      countObserver.observe(el);
    });
  };

  /* ------------------------------------------------------------------
     Hero: parallax leve (desktop)
  ------------------------------------------------------------------ */
  const initHero = () => {
    const hero = document.querySelector('.aura-hero');
    const media = hero?.querySelector('.aura-hero__media');
    if (!media || hero.dataset.auraReady) return;
    hero.dataset.auraReady = 'true';

    let ticking = false;
    let idle;

    const update = () => {
      ticking = false;
      if (reduced() || !desktopMq.matches) {
        media.style.transform = '';
        return;
      }
      const height = hero.offsetHeight;
      const y = Math.min(Math.max(window.scrollY, 0), height);
      media.style.transform = `translate3d(0, ${y * 0.15}px, 0)`;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (reduced() || !desktopMq.matches) return;
        media.style.willChange = 'transform';
        clearTimeout(idle);
        idle = setTimeout(() => (media.style.willChange = ''), 200);
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  };

  /* ------------------------------------------------------------------
     Header: encolhe ao rolar e esconde em rolagem rápida para baixo
  ------------------------------------------------------------------ */
  let headerReady = false;
  const initHeader = () => {
    const header = document.querySelector('#header-component');
    if (!header || headerReady) return;
    headerReady = true;

    let lastY = window.scrollY;
    let lastT = performance.now();
    let ticking = false;

    const update = () => {
      ticking = false;
      if (reduced()) {
        root.classList.remove('aura-header-compact', 'aura-header-hidden');
        return;
      }
      const y = Math.max(window.scrollY, 0);
      const now = performance.now();
      const dy = y - lastY;
      const dt = Math.max(now - lastT, 1);

      root.classList.toggle('aura-header-compact', y > 80);

      const busy = header.matches(':hover, :focus-within') || header.querySelector('[aria-expanded="true"]');
      if (y <= 120 || dy < -4 || busy) {
        root.classList.remove('aura-header-hidden');
      } else if (dy > 12 && dy / dt > 0.9) {
        root.classList.add('aura-header-hidden');
      }

      if (dy !== 0) {
        lastY = y;
        lastT = now;
      }
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();

    // Carrinho: bounce no ícone e pulso no contador quando a quantidade sobe
    const count = () => header.querySelector('.cart-bubble__text-count')?.textContent.trim() || '';
    let lastCount = count();
    new MutationObserver(() => {
      const now = count();
      if (now === lastCount) return;
      const increased = Number(now || 0) > Number(lastCount || 0);
      lastCount = now;
      if (!increased || reduced()) return;
      const icon = header.querySelector('.header-actions__cart-icon');
      if (!icon) return;
      icon.classList.remove('aura-bounce');
      void icon.offsetWidth;
      icon.classList.add('aura-bounce');
      setTimeout(() => icon.classList.remove('aura-bounce'), 900);
    }).observe(header, { childList: true, subtree: true, characterData: true });
  };

  /* Card de produto: feedback "Adicionado" */
  document.addEventListener('submit', (event) => {
    const wrapper = event.target.closest?.('.aura-card__form');
    if (!wrapper) return;
    const button = wrapper.querySelector('.aura-card__button');
    if (!button || button.disabled) return;
    button.setAttribute('data-aura-label', `✓ ${wrapper.dataset.addedLabel || ''}`.trim());
    button.classList.add('is-added');
    clearTimeout(button.auraTimer);
    button.auraTimer = setTimeout(() => button.classList.remove('is-added'), 1500);
  });

  /* ------------------------------------------------------------------
     Popup de captura (cupom): abre após o atraso, lembra o fechamento por 7 dias
  ------------------------------------------------------------------ */
  const initPopup = () => {
    const popup = document.querySelector('[data-aura-popup]');
    if (!popup || popup.dataset.auraReady) return;
    popup.dataset.auraReady = 'true';

    const KEY_CLOSED = 'aura_popup_closed';
    const KEY_DONE = 'aura_popup_done';
    const KEY_BADGE_OFF = 'aura_popup_badge_off';
    const WEEK = 7 * 24 * 60 * 60 * 1000;
    const store = {
      get: (key) => {
        try {
          return localStorage.getItem(key);
        } catch (err) {
          return null;
        }
      },
      set: (key, value) => {
        try {
          localStorage.setItem(key, value);
        } catch (err) {}
      },
    };

    const dialog = popup.querySelector('[role="dialog"]');
    const success = popup.querySelector('[data-aura-popup-success]');
    const badge = document.querySelector('[data-aura-popup-badge]');
    let opener = null;
    let isOpen = false;

    const badgeAllowed = () => !success && !store.get(KEY_DONE) && !store.get(KEY_BADGE_OFF);
    const showBadge = () => {
      if (badge) badge.hidden = !badgeAllowed();
    };

    const focusables = () =>
      [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled])')].filter(
        (el) => el.offsetParent !== null
      );

    // Só o X fecha o popup: o teclado apenas mantém o foco preso
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const lastItem = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        first.focus();
      }
    };

    const open = () => {
      if (isOpen) return;
      isOpen = true;
      opener = document.activeElement;
      if (badge) badge.hidden = true;
      popup.hidden = false;
      root.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      void popup.offsetWidth;
      popup.classList.add('is-open');
      document.addEventListener('keydown', onKey);
      (popup.querySelector('input:not([type="hidden"])') || dialog).focus({ preventScroll: true });
    };

    const close = () => {
      if (!isOpen) return;
      isOpen = false;
      popup.classList.remove('is-open');
      document.removeEventListener('keydown', onKey);
      if (!success) store.set(KEY_CLOSED, String(Date.now()));
      const finish = () => {
        popup.hidden = true;
        root.style.overflow = '';
        document.body.style.overflow = '';
        showBadge();
        const target = badge && !badge.hidden ? badge.querySelector('[data-aura-popup-badge-open]') : opener;
        target?.focus?.({ preventScroll: true });
      };
      if (reduced()) finish();
      else setTimeout(finish, 400);
    };

    popup.querySelector('[data-aura-popup-close]')?.addEventListener('click', close);
    badge?.querySelector('[data-aura-popup-badge-open]')?.addEventListener('click', open);
    badge?.querySelector('[data-aura-popup-badge-close]')?.addEventListener('click', () => {
      store.set(KEY_BADGE_OFF, '1');
      badge.hidden = true;
    });

    // Estado de sucesso: reabre na hora e nunca mais mostra o formulário
    if (success) {
      store.set(KEY_DONE, '1');
      open();
    } else {
      if (store.get(KEY_DONE) || store.get(KEY_BADGE_OFF)) return;
      const closedAt = Number(store.get(KEY_CLOSED) || 0);
      // Já fechou antes: o selo volta; o popup só reabre sozinho depois de 7 dias
      if (closedAt) showBadge();
      if (!closedAt || Date.now() - closedAt >= WEEK) {
        const delay = Math.max(0, Number(popup.dataset.delay) || 0) * 1000;
        setTimeout(open, delay);
      }
    }

    // Copiar código
    const copyButton = popup.querySelector('[data-aura-popup-copy]');
    copyButton?.addEventListener('click', async () => {
      const text = copyButton.dataset.copyText || '';
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        const field = document.createElement('textarea');
        field.value = text;
        field.setAttribute('readonly', '');
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        try {
          document.execCommand('copy');
        } catch (e) {}
        field.remove();
      }
      copyButton.textContent = copyButton.dataset.labelDone || '';
      clearTimeout(copyButton.auraTimer);
      copyButton.auraTimer = setTimeout(() => (copyButton.textContent = copyButton.dataset.label || ''), 2500);
    });

    // Formulário: máscara e validação do WhatsApp, tag e consentimento
    const form = popup.querySelector('form');
    const phone = form?.querySelector('[data-aura-popup-phone]');
    if (!form || !phone) return;
    const tags = form.querySelector('[data-aura-popup-tags]');
    const consent = form.querySelector('[data-aura-popup-consent]');
    const email = form.querySelector('input[type="email"]');
    const messages = form.querySelector('[data-aura-popup-msg-email]')?.dataset || {};
    const baseTags = tags.value;

    const setError = (name, text) => {
      const el = form.querySelector(`[data-aura-popup-error="${name}"]`);
      if (el) el.textContent = text || '';
    };

    phone.addEventListener('input', () => {
      const d = phone.value.replace(/\D/g, '').slice(0, 11);
      let out = d;
      if (d.length > 10) out = `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
      else if (d.length > 6) out = `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      else if (d.length > 2) out = `(${d.slice(0, 2)}) ${d.slice(2)}`;
      else if (d.length) out = `(${d}`;
      phone.value = out;
      setError('phone', '');
    });
    email?.addEventListener('input', () => {
      setError('email', '');
      email.removeAttribute('aria-invalid');
    });
    consent?.addEventListener('change', () => setError('consent', ''));

    form.addEventListener('submit', (e) => {
      const digits = phone.value.replace(/\D/g, '');
      let valid = true;
      if (!email.value.trim() || !email.checkValidity()) {
        setError('email', messages.auraPopupMsgEmail);
        email.setAttribute('aria-invalid', 'true');
        valid = false;
      }
      if (digits.length < 10 || digits.length > 11) {
        setError('phone', messages.auraPopupMsgPhone);
        valid = false;
      }
      if (!consent.checked) {
        setError('consent', messages.auraPopupMsgConsent);
        valid = false;
      }
      if (!valid) {
        e.preventDefault();
        form.querySelector('[aria-invalid="true"], .aura-popup__error:not(:empty)')?.closest('.aura-popup__field')?.querySelector('input')?.focus();
        return;
      }
      tags.value = `${baseTags},whatsapp:55${digits}`;
    });
  };

  /* ------------------------------------------------------------------
     Inicialização
  ------------------------------------------------------------------ */
  const initAll = (scope = document) => {
    each(scope, '[data-aura-carousel]', initCarousel);
    each(scope, '[data-aura-ugc]', initUgc);
    each(scope, '[data-aura-pm]', initProductMain);
    each(scope, '[data-aura-locator]', initLocator);
    initPopup();
    initVideos(scope);
    initAccordions(scope);
    each(scope, '[data-aura-moment]', initMoment);
    initOffer(scope);
    initFilters(scope);
    initDraw(scope);
    initCount(scope);
    initReveal(scope);
  };

  initAll();
  initHero();
  initHeader();

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
    initHero();
    initHeader();
  });
})();
